/**
 * Site Configuration
 * 
 * This file contains the core configuration for the AdSights website.
 * It defines SEO metadata, navigation structure, branding elements,
 * and social media presence to ensure consistent site-wide settings.
 */

export const siteConfig = {
  // Core brand identity
  name: "AdSights",
  url: "https://www.adsights.ai",
  foundingYear: "2024",
  themeColor: "#4F46E5",

  // Brand alternate names — emitted on the Organization, Brand, and
  // WebSite JSON-LD entities so search engines and AI assistants can
  // resolve the variants users actually type back to the canonical
  // brand entity. "Adsight" (singular) shows ~147 monthly impressions
  // in GSC at pos ~6.6 with ~0.7% CTR — including it as an
  // alternateName signals Google that the singular spelling refers to
  // the same entity, improving sitelink and knowledge-panel
  // eligibility for those queries. Keep this list conservative: only
  // genuine spelling/casing variants of the canonical brand. Bare
  // domains (e.g. "adsights.ai") are excluded — schema.org validators
  // soft-warn on them, and they risk surfacing as "Adsights.ai" (TLD
  // visible) in knowledge-panel name candidates.
  alternateNames: [
    "AdSights AI",
    "Adsight",
  ],
  
  // SEO descriptions - optimized for different contexts
  description: "AdSights is an AI-powered creative analytics platform that helps advertisers optimize ad performance through data-driven insights, creative testing, and performance tracking. Our platform enables marketers to make data-backed decisions that increase ROI and campaign effectiveness.",
  shortDescription: "AI-powered creative analytics for better ad performance",
  defaultCategory: "Marketing Software",
  
  // Primary SEO keywords - categorized for better organization
  keywords: [
    // Core product keywords
    "creative analytics",
    "ad optimization",
    "creative testing",
    "ad performance analysis",
    "creative performance optimization",
    
    // Technology keywords
    "AI advertising",
    "AI-powered ad insights",
    "AI marketing tools",
    "advertising technology",
    "adtech",
    
    // Marketing strategy keywords
    "data-driven marketing",
    "creative strategy",
    "digital advertising",
    "paid media optimization",
    "campaign optimization",
    
    // Performance keywords
    "ROI tracking",
    "conversion optimization",
    "ad performance",
    "video ad analytics",
    
    // Content type keywords
    "ad creative",
    "ad insights",
    "creative insights",
    "advertising insights",
    
    // Industry terms
    "marketing analytics",
    "marketing analytics software",
    "advertising optimization",
  ],
  
  // Site navigation structure
  baseLinks: {
    home: "/",
    about: "/about",
    pricing: "/pricing",
    terms: "/terms",
    privacy: "/privacy",
    returns: "/returns",
    shipping: "/shipping",
    dataPrivacy: "/data-privacy",
    privacyChoices: "/privacy/choices",
    license: "/license",
    requestAccess: "/request-access", // Primary early-access / lead funnel
    waitlist: "/waitlist", // Keep legacy route for any existing bookmarks/links
    blog: "/blog",
    resources: "/resources",
    // Product-family hub — the cohesive funnel top that presents BOTH
    // frameworks (ads + email) and their skills-only tiers. The nav
    // "Frameworks" entry and footer "Frameworks" group point here so
    // neither product is orphaned in discovery.
    frameworks: "/frameworks",
    // Express "early decider" checkout — a SKU picker for buyers who
    // already know what they want and don't need the full landing page.
    checkout: "/checkout",
    // Phase 3 — ads-framework storefront surfaces
    adsFramework: "/frameworks/ads-framework",
    adsFrameworkSetup: "/frameworks/ads-framework/setup",
    adsFrameworkSkills: "/frameworks/ads-framework/skills",
    // Lower-tier zip-only Skills bundle for non-technical buyers ($99).
    adsSkills: "/frameworks/ads-skills",
    adsSkillsSetup: "/frameworks/ads-skills/setup",
    // Email framework storefront surfaces ($149 full / $79 zip-only skills).
    emailFramework: "/frameworks/email-framework",
    emailFrameworkSetup: "/frameworks/email-framework/setup",
    emailSkills: "/frameworks/email-skills",
    account: "/account",
    signIn: "/auth/sign-in",
  },
  
  // Homepage hero promo video — the brand teaser that plays on the
  // homepage when the user clicks the poster overlay. Centralised so
  // every consumer references one source of truth (the inline
  // <video poster src>, the <video> in the SSR crawler-discoverable
  // fallback, the homepage VideoObject JSON-LD, the dedicated
  // `/watch/adsights-platform-demo` watch page + its `<video:video>`
  // sitemap extension, the og:video tuple, and the og:image dimensions
  // all read from this record). NB: the hero's canonical *watch page*
  // is `/watch/adsights-platform-demo` (see `~/lib/videos/watchable`),
  // not `/` — the homepage embed points its `url`/`WatchAction` there.
  //
  // `durationSeconds` was measured from the MP4's `mvhd` atom.
  // `width` / `height` are the video's encoded pixel dimensions
  // (also from `mvhd`). `posterWidth` / `posterHeight` are the
  // hero-dark.webp / hero-dark.jpg dimensions (`file public/images/hero-dark.*`).
  // Refresh all four if the underlying artifact changes.
  homepageHeroVideo: {
    contentPath: "/videos/adsights-demo-promo-video.mp4",
    // `posterPath` is the full-res WebP master for on-page / VideoObject use.
    // Do NOT point og:image / twitter:image at this path — LinkedIn-class
    // scrapers and X's card fetcher silently drop WebP social cards
    // (see `posterSocialPath` + the social-card WebP tripwires).
    posterPath: "/images/hero-dark.webp",
    // JPEG sibling of `posterPath` for crawler-visible social meta
    // (`og:image` / `twitter:image` on `/`). Generated by
    // `scripts/convert-og-webp-to-jpg.ts` (same settings as the rest of the
    // social-card JPEG batch). Keep dimensions in lock-step with
    // `posterWidth` / `posterHeight`.
    posterSocialPath: "/images/hero-dark.jpg",
    // `posterDisplayPath` is the display-sized (1136px) candidate used for the
    // on-page `<video poster>` attribute. The hero video underlays the LCP
    // `<HeroImage>` and renders at ≤1136 CSS px, so serving the 2432 master as
    // the poster loaded an oversized image (tripping Lighthouse
    // `uses-responsive-images`) AND pre-cached the master, which the theme
    // override's `<img>` then reused instead of its own 1136 candidate. Pointing
    // the poster at the 1136 file keeps the underlay display-matched; schema
    // keeps the WebP master via `posterPath`, social meta uses
    // `posterSocialPath`. Produced by `pnpm optimize:hero`.
    posterDisplayPath: "/images/hero-dark-1136.webp",
    title: "AdSights Platform Demo — AI-Powered Ad Creative Analytics",
    description:
      "60-second walkthrough of the AdSights platform: AI-powered creative analysis, cross-platform performance metrics, and actionable optimization recommendations for video and image ad campaigns.",
    durationSeconds: 68,
    width: 1920,
    height: 1080,
    posterWidth: 2432,
    posterHeight: 1365,
    ariaLabel:
      "AdSights platform demo — 60-second walkthrough of AI-powered ad creative analytics",
    // Schema.org `VideoObject.uploadDate` requires an ISO-8601 datetime
    // with a timezone offset — date-only values fail Search Console's
    // "Top non-critical issues" check ("Invalid datetime value for
    // uploadDate · Datetime property uploadDate is missing a timezone").
    // Use `T00:00:00Z` (UTC) so the value is unambiguous to every
    // structured-data parser. `r3317462982`.
    // Bumped 2026-07-07: the MP4 behind this stable URL was re-encoded
    // (35.8 MB / 4.2 Mbps → 16.5 MB / 1.9 Mbps, same 1080p content).
    // Per providers/videos.ts, `uploadDate` is the out-of-band freshness
    // floor that tells crawlers the bytes at /videos/… changed.
    uploadDate: "2026-07-07T00:00:00Z",
  },

  // Localization settings
  defaultLocale: 'en-US',
  
  // Social media and sharing
  twitterHandle: '@adsightsai',
  // facebookAppId: 'facebook-app-id',
  // Global OG / Twitter fallbacks MUST be PNG/JPEG — never WebP. Point at the
  // canonical share card (same asset as `shareCardPath` / `defaultImages.home`)
  // so a page that forgets to set images still gets a scraper-safe preview
  // instead of the legacy 4-bit palette PNGs at /og-image.png + /twitter-image.png.
  ogImage: "/images/adsights-ai-ad-creative-analysis-social-share-card.jpg",
  twitterImage: "/images/adsights-ai-ad-creative-analysis-social-share-card.jpg",
  
  // Social media profiles - for footer and structured data
  socialLinks: {
    twitter: "https://twitter.com/adsightsai",
    x: "https://x.com/adsightsai",
    // linkedin: "https://www.linkedin.com/company/104733209/",
    linkedin: "https://www.linkedin.com/company/adsights-ai/",
    // youtube: "https://www.youtube.com/channel/UCxVz3jRd_SkroIGnuyYzqfQ",
    youtube: "https://www.youtube.com/@AdSights",
    // Brand community subreddit (Reddit Pro / Links program — see
    // docs/marketing/reddit/). Reddit is a high-weight AI-search citation
    // surface (most-cited domain across AI engines per the 2026 analysis cited
    // in that runbook), so the subreddit is both a footer destination and a
    // first-class `sameAs` brand-entity anchor.
    reddit: "https://www.reddit.com/r/AdSights",
    // Brand's Reddit account (the org's identity on Reddit, analogous to the
    // X/LinkedIn handle) — distinct from the `reddit` subreddit above (the
    // community page). Both are official, so both are listed as `sameAs`
    // anchors: per schema.org guidance there's no penalty for multiple URLs on
    // one platform, and naming both the account and the community removes
    // entity-resolution ambiguity for the high-weight reddit.com domain.
    redditProfile: "https://www.reddit.com/user/AdSights/",
    pinterest: "https://pinterest.com/adsightsai/",
    github: "https://github.com/adsights-ai",
    tiktok: "https://tiktok.com/@adsightsai",
    medium: "https://medium.com/@adsights",
    huggingface: "https://huggingface.co/AdSights",
    trustpilot: "https://www.trustpilot.com/review/adsights.ai",
    // Capterra product listing — LIVE (verified 2026-07-11). Part of the
    // review-platform rollout across the G2 network (G2 acquired Capterra,
    // GetApp, and Software Advice in Feb 2026); structured-review databases
    // carry high AI-citation / Knowledge-Graph weight, same rationale as
    // Trustpilot above. Canonical listing copy + per-platform status:
    // docs/marketing/review-platform-listings.md. When the G2 / GetApp /
    // Software Advice profile URLs go live and are verified, add each here
    // and in getOrganizationSameAs() below (one line each) — do NOT guess
    // or construct those URLs before then.
    capterra: "https://www.capterra.com/p/10049701/AdSights/",
    // TheOrg — public org-chart / company-structure database. Like
    // Crunchbase/Wellfound, a structured company-entity surface AI search
    // engines and the Knowledge Graph walk when resolving brand identity.
    // Verified live 2026-07-18 (final 200, no redirect).
    theOrg: "https://theorg.com/org/adsights",
    crunchbase: "https://www.crunchbase.com/organization/adsights",
    // Wellfound (formerly AngelList Talent) company profile — a
    // structured startup/company database AI search engines and the
    // Knowledge Graph walk when resolving brand-entity identity, in the
    // same vein as Crunchbase. A first-class `sameAs` anchor.
    wellfound: "https://wellfound.com/company/adsights",
    stackshare: "https://stackshare.io/companies/adsights",
    observable: "https://observablehq.com/@adsights",
    // Canonical ProductHunt profile aggregating launches. AdSights's
    // first launch ("AdSights Free Design Tools" — the 11-generator
    // suite this PR's variants are derived from) lives at
    // /products/adsights-free-design-tools and is referenced from
    // src/content/blog/free-design-generators-product-hunt-launch.mdx;
    // a second launch is planned. ProductHunt carries documented
    // AI-citation weight for product-tooling brands in Perplexity,
    // ChatGPT search, and the Google AI Mode citation graph — the
    // structured-review surface is one of the high-signal sameAs
    // anchors crawlers walk when resolving brand-entity identity.
    productHunt: "https://www.producthunt.com/products/adsights-free-design-tools",
    // The ads-framework launch's own ProductHunt product page — distinct
    // from `productHunt` above (the first launch). This is a PERMANENT
    // entity-grounding URL: the framework page's SoftwareApplication
    // `sameAs` and the durable "View on Product Hunt" hero link read it
    // from here — NOT from the time-boxed `launch-promo` module — so
    // retiring the PH2026 promo can never strip the lasting grounding or
    // the durable link. `launch-promo` re-exports it for the banner's
    // convenience.
    productHuntAdsFramework:
      "https://www.producthunt.com/products/adsights-ads-framework",
    // facebook: "https://facebook.com/adsightsai",
    // facebook: "https://www.facebook.com/profile.php?id=61563392094948"
  },
  
  // Canonical share card — used as the OpenGraph fallback for every
  // primary page, the framework hub's sitemap `<image:image>`
  // extension, and the sitemap-image entries on the marketing pages
  // that don't have their own hero photo. Centralised so a future
  // rebrand lands in one file instead of grep-and-replace.
  shareCardPath:
    "/images/adsights-ai-ad-creative-analysis-social-share-card.jpg",

  // Default images for core pages
  defaultImages: {
    // Main pages
    home: "/images/adsights-ai-ad-creative-analysis-social-share-card.jpg",
    about: "/images/adsights-ai-ad-creative-analysis-social-share-card.jpg",
    pricing: "/images/adsights-ai-ad-creative-analysis-social-share-card.jpg",
    terms: "/images/adsights-ai-ad-creative-analysis-social-share-card.jpg",
    privacy: "/images/adsights-ai-ad-creative-analysis-social-share-card.jpg",
    requestAccess: "/images/adsights-ai-ad-creative-analysis-social-share-card.jpg",
    
    // Blog images
    blog: "/images/blog/adsights-digital-marketing-insights-creative-analytics-blog-og-image.jpg",
    
    // Resources
    resources: "/images/resources/adsights-digital-marketing-resource-center-home-hero-image.jpg",
    resourcesTools: "/images/resources/tools/adsights-digital-marketing-resource-center-interactive-tools-marketing-calculators-quizzes-analyzers-hero-image.jpg",
    resourcesGuides: "/images/resources/guides/adsights-digital-marketing-resource-center-guides-tutorials-hero-image.jpg",
    resourcesTemplates: "/images/resources/templates/adsights-digital-marketing-resource-center-templates-hero-image.jpg",
    resourcesEbooks: "/images/resources/ebooks/adsights-digital-marketing-resource-center-ebooks-hero-image.jpg",
    resourcesGlossary: "/images/resources/glossary/adsights-digital-marketing-resource-center-glossary-terms-hero-image.jpg",
    resourcesCaseStudies: "/images/resources/case-studies/adsights-digital-marketing-resource-center-case-studies-hero-image.jpg",
    
    // Default fallback — scraper-safe JPEG (never WebP / never the legacy
    // 4-bit /og-image.png palette PNG that X's card fetcher can drop).
    default: "/images/adsights-ai-ad-creative-analysis-social-share-card.jpg"
  }
}

export type siteConfig = typeof siteConfig

/**
 * Canonical `sameAs` URL list for Organization / Brand JSON-LD,
 * derived from `siteConfig.socialLinks` so the rendered Footer,
 * Organization structured data, About-page entity references, and
 * any other consumer cannot drift apart. Order is conventional /
 * human-readable for diff legibility; per the schema.org
 * specification (https://schema.org/sameAs), the field is an
 * unordered list of URLs — crawlers do not weight by position.
 *
 * Trustpilot is included despite being lower-traffic on first-party
 * surfaces: AI search engines (ChatGPT, Perplexity, Google AI
 * Overviews) ground brand-identity claims more heavily against
 * structured-review reputation databases than against first-party
 * social profiles. Same logic applies to Crunchbase (Knowledge
 * Graph anchor) and StackShare (technical brand surface).
 *
 * Any profile rotation (handle change, URL migration) updates the
 * `socialLinks` entry once; all consumers — Footer,
 * GlobalStructuredData, WithPageStructuredData providers, etc —
 * pick it up automatically.
 */
export function getOrganizationSameAs(): string[] {
  const s = siteConfig.socialLinks
  return [
    s.linkedin,
    s.crunchbase,
    // Wellfound — startup/company database entity anchor (see siteConfig.socialLinks.wellfound).
    s.wellfound,
    s.x,
    s.youtube,
    // Reddit — among the most-cited domains in AI search; the brand subreddit is
    // a strong entity-grounding anchor (see siteConfig.socialLinks.reddit).
    s.reddit,
    // Reddit brand account — the org's account-identity edge on the same
    // high-weight domain (see siteConfig.socialLinks.redditProfile).
    s.redditProfile,
    s.trustpilot,
    // Capterra — structured software-review database (G2 network), verified
    // live 2026-07-11. Future G2 / GetApp / Software Advice profiles join
    // here once live (see docs/marketing/review-platform-listings.md).
    s.capterra,
    // TheOrg — structured org-chart / company database entity anchor,
    // verified live 2026-07-18 (see siteConfig.socialLinks.theOrg).
    s.theOrg,
    // ProductHunt — high AI-citation weight for product-tooling
    // brands. Verified live profile aggregating AdSights launches
    // (see siteConfig.socialLinks comment + the launch blog post).
    s.productHunt,
    // Second ProductHunt launch — the ads-framework product page (distinct
    // from the first-launch profile above). Grounds the brand entity to both
    // launches on a high-citation product-review surface.
    s.productHuntAdsFramework,
    s.github,
    s.tiktok,
    s.pinterest,
    s.medium,
    s.observable,
    s.huggingface,
    s.stackshare,
  ].filter((url): url is string => typeof url === 'string' && url.length > 0)
}
